import React from "react";
import { Document, Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer";

// Register fonts if needed
// Font.register({ family: 'Inter', src: '/fonts/Inter-Regular.ttf' });

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 30,
    textAlign: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1f2937',
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 20,
  },
  section: {
    marginBottom: 25,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1f2937',
    borderBottom: '1 solid #e5e7eb',
    paddingBottom: 5,
  },
  table: {
    marginTop: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #e5e7eb',
    paddingVertical: 8,
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
    fontWeight: 'bold',
  },
  tableCell: {
    flex: 1,
    paddingHorizontal: 8,
    fontSize: 10,
  },
  tableCellMonth: {
    flex: 0.5,
    paddingHorizontal: 8,
    fontSize: 10,
  },
  tableCellHeadcount: {
    flex: 0.8,
    paddingHorizontal: 8,
    fontSize: 10,
  },
  tableCellCost: {
    flex: 1,
    paddingHorizontal: 8,
    fontSize: 10,
    textAlign: 'right',
  },
  summaryBox: {
    backgroundColor: '#f3f4f6',
    padding: 15,
    marginTop: 20,
    borderRadius: 8,
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1f2937',
  },
  summaryGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  summaryItem: {
    flex: 1,
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#059669',
  },
  summaryLabel: {
    fontSize: 10,
    color: '#6b7280',
    marginTop: 2,
  },
});

interface TeamRole {
  id: string;
  name: string;
  salary: number;
  hourlyRate?: number; // optional hourly rate in USD, used to auto-calculate salary
  headcount: number[];
  category: string;
}

interface OperatingCost {
  id: string;
  name: string;
  monthlyCosts: number[];
  category: string;
}

interface CostBreakdownPDFProps {
  projectName: string;
  teamRoles: TeamRole[];
  operatingCosts: OperatingCost[];
  projectionMonths: number;
  calculateTotalHeadcount: (monthIndex: number) => number;
  calculateMonthlySalaryCost: (monthIndex: number) => number;
  calculateMonthlyOperatingCost: (monthIndex: number) => number;
  calculateTotalMonthlyCost: (monthIndex: number) => number;
  formatCurrency: (value: number) => string;
}

export const CostBreakdownPDF: React.FC<CostBreakdownPDFProps> = ({
  projectName,
  teamRoles,
  operatingCosts,
  projectionMonths,
  calculateTotalHeadcount,
  calculateMonthlySalaryCost,
  calculateMonthlyOperatingCost,
  calculateTotalMonthlyCost,
  formatCurrency,
}) => {

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{projectName} - Cost Breakdown</Text>
          <Text style={styles.subtitle}>22-Month Financial Projection Report</Text>
        </View>

        {/* Summary */}
        <View style={styles.summaryBox}>
          <Text style={styles.summaryTitle}>Key Metrics</Text>
          <View style={styles.summaryGrid}>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>
                {formatCurrency(calculateTotalMonthlyCost(5))}
              </Text>
              <Text style={styles.summaryLabel}>Month 6 Cost</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>
                {formatCurrency(calculateTotalMonthlyCost(11))}
              </Text>
              <Text style={styles.summaryLabel}>Month 12 Cost</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>
                {calculateTotalHeadcount(17)}
              </Text>
              <Text style={styles.summaryLabel}>Total Headcount</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>
                {formatCurrency(
                  Array.from({ length: 22 }, (_, i) => calculateTotalMonthlyCost(i)).reduce(
                    (a, b) => a + b,
                    0
                  )
                )}
              </Text>
              <Text style={styles.summaryLabel}>22-Month Total</Text>
            </View>
          </View>
        </View>

        {/* Team Headcount Table */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Team Headcount & Salaries</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, { flex: 2 }]}>Role</Text>
              <Text style={styles.tableCell}>Monthly Salary</Text>
              <Text style={styles.tableCell}>Category</Text>
              <Text style={styles.tableCellMonth}>M6</Text>
              <Text style={styles.tableCellMonth}>M12</Text>
              <Text style={styles.tableCellMonth}>M22</Text>
            </View>
            {teamRoles.map((role) => (
              <View key={role.id} style={styles.tableRow}>
                <Text style={[styles.tableCell, { flex: 2 }]}>{role.name}</Text>
                <Text style={styles.tableCell}>{formatCurrency(role.salary)}</Text>
                <Text style={styles.tableCell}>{role.category}</Text>
                <Text style={styles.tableCellMonth}>{role.headcount[5] || 0}</Text>
                <Text style={styles.tableCellMonth}>{role.headcount[11] || 0}</Text>
                <Text style={styles.tableCellMonth}>{role.headcount[21] || 0}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Operating Costs Table */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Operating Costs</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, { flex: 2 }]}>Category</Text>
              <Text style={styles.tableCell}>Type</Text>
              <Text style={styles.tableCellMonth}>M6</Text>
              <Text style={styles.tableCellMonth}>M12</Text>
              <Text style={styles.tableCellMonth}>M22</Text>
            </View>
            {operatingCosts.map((cost) => (
              <View key={cost.id} style={styles.tableRow}>
                <Text style={[styles.tableCell, { flex: 2 }]}>{cost.name}</Text>
                <Text style={styles.tableCell}>{cost.category}</Text>
                <Text style={styles.tableCellMonth}>
                  {formatCurrency(cost.monthlyCosts[5] || 0)}
                </Text>
                <Text style={styles.tableCellMonth}>
                  {formatCurrency(cost.monthlyCosts[11] || 0)}
                </Text>
                <Text style={styles.tableCellMonth}>
                  {formatCurrency(cost.monthlyCosts[21] || 0)}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Monthly Summary Table */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Monthly Cost Summary</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={styles.tableCellMonth}>Month</Text>
              <Text style={styles.tableCellHeadcount}>Headcount</Text>
              <Text style={styles.tableCellCost}>Salary Costs</Text>
              <Text style={styles.tableCellCost}>Operating Costs</Text>
              <Text style={styles.tableCellCost}>Monthly Burn</Text>
            </View>
            {[5, 11, 17].map((monthIndex) => (
              <View key={monthIndex} style={styles.tableRow}>
                <Text style={styles.tableCellMonth}>{monthIndex + 1}</Text>
                <Text style={styles.tableCellHeadcount}>
                  {calculateTotalHeadcount(monthIndex)}
                </Text>
                <Text style={styles.tableCellCost}>
                  {formatCurrency(calculateMonthlySalaryCost(monthIndex))}
                </Text>
                <Text style={styles.tableCellCost}>
                  {formatCurrency(calculateMonthlyOperatingCost(monthIndex))}
                </Text>
                <Text style={styles.tableCellCost}>
                  {formatCurrency(calculateTotalMonthlyCost(monthIndex))}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};